<template>
  <DynamicDialog />
  <ConfirmDialog />
  <router-view v-if="isAuthLayout" />

  <div
    v-else
    id="top-container"
    class="h-screen w-screen overflow-y-scroll px-4 py-3 flex flex-col gap-3"
  >
    <div class="flex-none header">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-5 grow">
          <RouterLink to="/"><IconLogo class="w-auto h-10 logo" /></RouterLink>
          <Breadcrumb class="bg-transparent! py-1! text-lg" :home="home" :model="items" />
        </div>

        <Badge
          class="me-5 cursor-pointer"
          value="sudo"
          severity="success"
          v-if="userStore.me?.is_sudo"
          @click="sudoLogout()"
        ></Badge>

        <MoneyBalance class="me-5" />
        <button
          id="user-menu-button"
          class="flex items-center gap-3"
          @click="toggleUserMenue"
          type="button"
          aria-label="Profile"
        >
          <span id="user-menu-email" class="text-sm font-medium text-slate-900">
            {{ userStore.me?.email }}
          </span>
          <span
            id="user-menu-avatar"
            class="rounded-full border h-7.5 w-7.5 flex items-center justify-center bg-slate-200 border-slate-800"
          >
            <i class="pi pi-user text-sm text-slate-800"></i>
          </span>
        </button>
        <TieredMenu ref="userMenu" id="user-menue" :model="userMenuItems" popup />
      </div>
    </div>

    <Toast />

    <div class="bg-white w-full flex-1 shadow-lg rounded-3xl p-3 border border-slate-200">
      <router-view />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/stores/userStore'
import Breadcrumb from 'primevue/breadcrumb'
import { useRoute, useRouter } from 'vue-router'
import { computed, defineAsyncComponent, ref } from 'vue'
import IconLogo from '@/components/icon/IconLogo.vue'
import DynamicDialog from 'primevue/dynamicdialog'
import type { MenuItem } from 'primevue/menuitem'
import { useToast, type TieredMenu } from 'primevue'
import { useAuthStore } from './stores/authStore'
import { UserRoles } from './interface/gen/db'

const userStore = useUserStore()
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const userMenu = ref<typeof TieredMenu>()
const userMenuItems = ref<MenuItem[]>([
  {
    label: 'Администрирование',
    icon: 'pi pi-pen-to-square',
    visible: () => userStore.hasRole(userStore.me, UserRoles.Admin),
    command() {
      router.push({ name: 'admin' })
    },
  },
  {
    label: 'Выйти из SUDO',
    icon: 'pi pi-sign-out',
    visible: () => userStore.me?.is_sudo ?? false,
    command() {
      sudoLogout()
    },
  },
  {
    label: 'Выйти',
    icon: 'pi pi-sign-out',
    command(event) {
      userStore.logout()
    },
  },
])

const isAuthLayout = computed(() => route.meta?.layout === 'auth')

const home = {
  label: 'Главная',
  to: '/',
  url: '/',
}

const items = computed(() => {
  if (route.path.startsWith('/vacancy')) {
    return [{ label: 'Услуги и кандидаты' }]
  }
  return []
})

const toggleUserMenue = (event: Event) => {
  ;(userMenu.value as any).toggle(event)
}

async function sudoLogout() {
  const ret = await authStore.sudoLogout()
  if (ret.success) {
    document.location = '/admin'
  } else {
    toast.add({
      severity: 'error',
      summary: 'Ошибка sudo',
      detail: 'Не удалось переключиться в свой аккаунт',
      life: 3000,
    })
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Triodion&display=swap');

html,
body {
  padding: 0 !important;
  margin: 0 !important;
  min-width: 100vw;
  width: 100vw;
  min-height: 100vh;
  max-width: 100vw;
  overflow-x: hidden;

  font-family: 'Montserrat';
  background:
    radial-gradient(1200px 600px at 20% 10%, rgba(13, 110, 253, 0.1), transparent 60%),
    radial-gradient(900px 500px at 80% 90%, rgba(25, 135, 84, 0.1), transparent 55%),
    var(--bs-body-bg, #fff);
}

.main-wrapper {
  width: 100%;
}

#user-menu-button {
  cursor: pointer;

  #user-menu-avatar,
  #user-menu-email {
    transition: all 0.2s;
  }

  &:hover #user-menu-avatar {
    border-color: var(--p-primary-color);
  }
  &:hover #user-menu-email {
    color: var(--p-primary-color);
    border-radius: 8px;
  }
}

.header {
  top: -12px;
  position: sticky;
  z-index: 11;
  margin: 0 1px;

  animation-timeline: view();
  animation-name: whiteback;
  animation-range: entry 99vh exit 20vh;
  animation-fill-mode: forwards;
  background-color: transparent;
  box-shadow: 5px 5px 15px transparent;
  border-radius: 0;
  padding: 0;
}
@keyframes whiteback {
  from {
    background-color: transparent;
  }
  to {
    background-color: white;
    box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.2);
    border-radius: 0 0 12px 12px;

    padding: 4px 12px;
  }
}
</style>
